import type { EventView } from "@confluence/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { useI18n } from "../i18n";
import type { CalendarInfo } from "./data";
import { useEventChunks, useEvents } from "./data";
import { dayFmt, hourFmt, timeFmt, useNow } from "./format";
import { eventsOnDay, layoutBanners, layoutDay, spanOf, type Segment } from "./layout";
import { addDays, dayKeyIn, isoWeek, isWeekend, minutesIn, startOfMonth, startOfWeek, zonedInstant, type DayKey } from "./time";

export interface ViewProps {
  tz: string;
  today: DayKey;
  calendars: Map<string, CalendarInfo>;
  onOpen: (ev: EventView) => void;
  onPickDay: (key: DayKey, view?: "week" | "agenda") => void;
  /** Nouvel événement à ce jour (et à cette heure, en minutes depuis minuit). */
  onCreate?: (key: DayKey, minutes: number | null) => void;
}

/** Couleur de l'agenda et état de l'événement, communs à toutes les vues. */
function evStyle(ev: EventView, calendars: Map<string, CalendarInfo>): CSSProperties {
  return { ["--cal" as string]: calendars.get(ev.calendarId)?.color ?? "var(--accent)" };
}
function evClass(base: string, ev: EventView) {
  return [
    base,
    ev.transparency === "transparent" && "is-free",
    ev.status === "tentative" && "is-tentative",
    ev.declined && "is-declined",
    ev.busy && "is-busy",
    ev.block && "is-block",
  ]
    .filter(Boolean)
    .join(" ");
}

function useTitle() {
  const { t } = useI18n();
  return (ev: EventView) => (ev.busy ? t("share.busy") : ev.title || t("cal.noTitle"));
}

// ───── Semaine (grille horaire) ─────

const HOUR_PX = 48;
const PX_PER_MIN = HOUR_PX / 60;

type DragMode = "move" | "resize";
interface Drag {
  seg: Segment;
  day: DayKey;
  mode: DragMode;
  x: number;
  y: number;
  colW: number;
  dMin: number;
  dDay: number;
  moved: boolean;
}

export function WeekView({
  days,
  events,
  tz,
  today,
  calendars,
  onOpen,
  onPickDay,
  onCreate,
  canEdit,
  onMove,
  weekNumbers,
}: ViewProps & { days: DayKey[]; events: EventView[]; weekNumbers: boolean; canEdit: (ev: EventView) => boolean; onMove: (ev: EventView, start: Date, end: Date) => void }) {
  const { t, locale } = useI18n();
  const title = useTitle();
  const now = useNow();
  const scroller = useRef<HTMLDivElement>(null);
  const { banners, lanes } = useMemo(() => layoutBanners(events, days, tz), [events, days, tz]);
  const columns = useMemo(() => days.map((key) => layoutDay(events, key, tz)), [events, days, tz]);
  const showsToday = days.includes(today);

  // Glisser pour déplacer (heure et jour), poignée du bas pour changer la durée ; pas de 15 minutes.
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const ignoreClickUntil = useRef(0);
  const startDrag = (e: PointerEvent, seg: Segment, day: DayKey, mode: DragMode) => {
    if (e.button !== 0 || e.pointerType === "touch" || !canEdit(seg.ev)) return;
    const span = spanOf(seg.ev, tz);
    if (span.first !== span.last) return;
    const el = (e.currentTarget as HTMLElement).closest(".ev") as HTMLElement;
    const col = el.closest(".wk-col") as HTMLElement;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // pointeur déjà relâché : le glisser s'arrêtera au prochain pointerup
    }
    dragRef.current = { seg, day, mode, x: e.clientX, y: e.clientY, colW: col.getBoundingClientRect().width, dMin: 0, dDay: 0, moved: false };
    if (mode === "resize") e.stopPropagation();
  };
  const moveDrag = (e: PointerEvent) => {
    const r = dragRef.current;
    if (!r) return;
    const dx = e.clientX - r.x;
    const dy = e.clientY - r.y;
    if (!r.moved && Math.hypot(dx, dy) < 5) return;
    r.moved = true;
    const index = days.indexOf(r.day);
    r.dDay = r.mode === "move" ? Math.max(-index, Math.min(days.length - 1 - index, Math.round(dx / r.colW))) : 0;
    const raw = Math.round(dy / PX_PER_MIN / 15) * 15;
    r.dMin =
      r.mode === "move"
        ? Math.max(-r.seg.top, Math.min(24 * 60 - r.seg.bottom, raw))
        : Math.max(15 - (r.seg.bottom - r.seg.top), Math.min(24 * 60 - r.seg.bottom, raw));
    setDrag({ ...r });
  };
  const endDrag = () => {
    const r = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!r?.moved) return;
    ignoreClickUntil.current = Date.now() + 300;
    if (r.dMin === 0 && r.dDay === 0) return;
    const ev = r.seg.ev;
    const duration = new Date(ev.end).getTime() - new Date(ev.start).getTime();
    if (r.mode === "move") {
      const start = zonedInstant(addDays(r.day, r.dDay), minutesIn(ev.start, tz) + r.dMin, tz);
      onMove(ev, start, new Date(start.getTime() + duration));
    } else {
      onMove(ev, new Date(ev.start), new Date(new Date(ev.end).getTime() + r.dMin * 60_000));
    }
  };

  // À l'ouverture : l'heure courante si aujourd'hui est visible, sinon le début de matinée.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const minutes = showsToday ? minutesIn(Date.now(), tz) - 90 : 7 * 60 + 30;
    el.scrollTop = Math.max(0, minutes * PX_PER_MIN);
  }, [days[0], showsToday]);

  const grid = { ["--days" as string]: days.length, minWidth: days.length >= 5 ? 640 : undefined } as CSSProperties;
  return (
    <div className="wk" style={grid}>
      <div className="wk-head">
        <span className="wk-gutter tnum">{weekNumbers && t("cal.weekNumber", { n: String(isoWeek(days[0]!)) })}</span>
        {days.map((key) => (
          <button
            key={key}
            className={`wk-day${key === today ? " is-today" : ""}${isWeekend(key) ? " is-we" : ""}`}
            onClick={() => onPickDay(key, "agenda")}
            aria-label={dayFmt(key, locale, { weekday: "long", day: "numeric", month: "long" })}
          >
            <span className="dw">{dayFmt(key, locale, { weekday: "short" })}</span>
            <span className="dn">{Number(key.slice(8))}</span>
          </button>
        ))}
      </div>
      {lanes > 0 && (
        <div className="wk-allday">
          <span className="wk-gutter">{t("common.allDay")}</span>
          <div className="wk-banners" style={{ gridTemplateRows: `repeat(${lanes}, 24px)` }}>
            {banners.map((b) => (
              <button
                key={b.ev.id}
                className={evClass("banner", b.ev) + (b.cutStart ? " cut-start" : "") + (b.cutEnd ? " cut-end" : "")}
                style={{ ...evStyle(b.ev, calendars), gridColumn: `${b.from + 1} / ${b.to + 2}`, gridRow: b.lane + 1 }}
                onClick={() => onOpen(b.ev)}
              >
                {title(b.ev)}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="wk-scroll" ref={scroller}>
        <div className="wk-body" style={{ height: 24 * HOUR_PX }}>
          <div className="wk-hours" aria-hidden="true">
            {Array.from({ length: 23 }, (_, i) => (
              <span key={i} style={{ top: (i + 1) * HOUR_PX }}>
                {hourFmt(i + 1, locale)}
              </span>
            ))}
          </div>
          {days.map((key, i) => (
            <div
              key={key}
              className={`wk-col${isWeekend(key) ? " is-we" : ""}${onCreate ? " can-create" : ""}`}
              onClick={(e) => {
                // Clic sur un créneau libre : nouvel événement à la demi-heure.
                if (!onCreate || e.target !== e.currentTarget) return;
                onCreate(key, Math.min(23 * 60 + 30, Math.floor(e.nativeEvent.offsetY / PX_PER_MIN / 30) * 30));
              }}
            >
              {columns[i]!.map((seg) => {
                const dragging = drag?.seg.ev.id === seg.ev.id && drag.day === key ? drag : null;
                const height = (seg.bottom - seg.top + (dragging?.mode === "resize" ? dragging.dMin : 0)) * PX_PER_MIN;
                const editable = canEdit(seg.ev);
                return (
                  <button
                    key={seg.ev.id}
                    className={evClass("ev", seg.ev) + (height < 36 ? " is-short" : "") + (dragging ? " is-dragging" : "") + (editable ? " is-editable" : "")}
                    onPointerDown={(e) => startDrag(e, seg, key, "move")}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={() => {
                      dragRef.current = null;
                      setDrag(null);
                    }}
                    style={{
                      ...evStyle(seg.ev, calendars),
                      top: seg.top * PX_PER_MIN,
                      height: height - 2,
                      ...(dragging?.mode === "move" ? { transform: `translate(${dragging.dDay * dragging.colW}px, ${dragging.dMin * PX_PER_MIN}px)` } : {}),
                      // Les événements qui se chevauchent se décalent en cascade, en restant lisibles.
                      left: `calc(${(seg.col / seg.cols) * 100}% + 2px)`,
                      width: `calc(${seg.col === seg.cols - 1 ? 100 / seg.cols : Math.min(100 - (seg.col / seg.cols) * 100, (100 / seg.cols) * 1.7)}% - 4px)`,
                      zIndex: seg.col + 1,
                    }}
                    onClick={() => Date.now() > ignoreClickUntil.current && onOpen(seg.ev)}
                  >
                    <span className="ev-t">{title(seg.ev)}</span>
                    <span className="ev-m tnum">
                      {timeFmt(seg.ev.start, locale, tz)}
                      {seg.ev.location && height >= 60 ? ` · ${seg.ev.location}` : ""}
                    </span>
                    {editable && height >= 30 && <span className="ev-grip" aria-hidden="true" onPointerDown={(e) => startDrag(e, seg, key, "resize")} />}
                  </button>
                );
              })}
              {key === today && <span className="nowline" style={{ top: minutesIn(now, tz) * PX_PER_MIN }} aria-hidden="true" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ───── Mois ─────

const MONTH_ITEMS = 3;

export function MonthView({ anchor, days, events, tz, today, calendars, onOpen, onPickDay, onCreate }: ViewProps & { anchor: DayKey; days: DayKey[]; events: EventView[] }) {
  const { t, locale } = useI18n();
  const title = useTitle();
  const month = anchor.slice(0, 7);
  return (
    <div className="mo">
      <div className="mo-dow" aria-hidden="true">
        {days.slice(0, 7).map((key) => (
          <span key={key}>{dayFmt(key, locale, { weekday: "short" })}</span>
        ))}
      </div>
      <div className="mo-grid">
        {days.map((key) => {
          const list = eventsOnDay(events, key, tz);
          const extra = list.length - MONTH_ITEMS;
          return (
            <div
              key={key}
              className={`mo-cell${key.slice(0, 7) !== month ? " is-out" : ""}${isWeekend(key) ? " is-we" : ""}${onCreate ? " can-create" : ""}`}
              onClick={(e) => onCreate && e.target === e.currentTarget && onCreate(key, null)}
            >
              <button
                className={`mo-n${key === today ? " is-today" : ""}`}
                onClick={() => onPickDay(key, "week")}
                aria-label={dayFmt(key, locale, { weekday: "long", day: "numeric", month: "long" })}
              >
                {Number(key.slice(8))}
              </button>
              {list.slice(0, extra > 0 ? MONTH_ITEMS - 1 : MONTH_ITEMS).map((ev) =>
                spanOf(ev, tz).banner ? (
                  <button key={ev.id} className={evClass("mo-bar", ev)} style={evStyle(ev, calendars)} onClick={() => onOpen(ev)}>
                    {title(ev)}
                  </button>
                ) : (
                  <button key={ev.id} className={evClass("mo-ev", ev)} style={evStyle(ev, calendars)} onClick={() => onOpen(ev)}>
                    <i aria-hidden="true" />
                    <span className="tnum">{dayKeyIn(ev.start, tz) === key ? timeFmt(ev.start, locale, tz) : "…"}</span> {title(ev)}
                  </button>
                ),
              )}
              {extra > 0 && (
                <button className="mo-more" onClick={() => onPickDay(key, "agenda")}>
                  {t("cal.more", { n: String(extra + 1) })}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───── Agenda (liste continue) ─────

const CHUNK = 28;

export function AgendaView({ anchor, bounds, shareIds, tz, today, calendars, onOpen }: ViewProps & { anchor: DayKey; bounds: [DayKey, DayKey]; shareIds: string[] }) {
  const { t, locale } = useI18n();
  const title = useTitle();
  const [start, setStart] = useState(anchor);
  const [chunks, setChunks] = useState(1);
  const end = addDays(start, chunks * CHUNK);
  const ranges = useMemo(
    () => Array.from({ length: chunks }, (_, i): [DayKey, DayKey] => [addDays(start, i * CHUNK), addDays(start, (i + 1) * CHUNK)]),
    [start, chunks],
  );
  const results = useEventChunks(ranges, tz, shareIds);
  const events = useMemo(() => results.data.filter((ev) => calendars.get(ev.calendarId)?.shown !== false), [results.data, calendars]);
  const loading = results.isPending;

  const list = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const canLater = end < bounds[1];
  const canEarlier = start > bounds[0];

  // Charge la suite quand le bas de la liste approche.
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canLater || loading) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && setChunks((c) => c + 1), { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [canLater, loading, chunks]);

  // Ajouter des jours au-dessus ne doit pas faire sauter la liste.
  const keepOffset = useRef<number | null>(null);
  const earlier = () => {
    const scroller = list.current?.closest(".cal-scroll");
    keepOffset.current = scroller ? scroller.scrollHeight - scroller.scrollTop : null;
    setStart((s) => addDays(s, -CHUNK));
    setChunks((c) => c + 1);
  };
  useLayoutEffect(() => {
    const scroller = list.current?.closest(".cal-scroll");
    if (scroller && keepOffset.current !== null) {
      scroller.scrollTop = scroller.scrollHeight - keepOffset.current;
      keepOffset.current = null;
    }
  }, [start]);

  const days = Array.from({ length: chunks * CHUNK }, (_, i) => addDays(start, i)).filter((k) => k < bounds[1]);
  return (
    <div className="ag" ref={list}>
      {canEarlier ? (
        <button className="btn btn-sm ag-earlier" onClick={earlier}>
          {t("cal.earlier")}
        </button>
      ) : (
        <p className="ag-edge muted">{t("cal.windowStart")}</p>
      )}
      {days.map((key) => {
        const items = eventsOnDay(events, key, tz);
        return (
          <section key={key} className={`ag-day${key === today ? " is-today" : ""}${key < today ? " is-past" : ""}${items.length ? "" : " is-empty"}`}>
            <h3 className="ag-date">
              <span className="dn">{Number(key.slice(8))}</span>
              <span className="dw">
                {dayFmt(key, locale, { weekday: "long" })}
                {(key === start || key === startOfMonth(key)) && <small>{dayFmt(key, locale, { month: "long", year: "numeric" })}</small>}
              </span>
            </h3>
            <div className="ag-rows">
              {items.length === 0 && <p className="muted ag-none">{t("home.nothing")}</p>}
              {items.map((ev) => {
                const banner = spanOf(ev, tz).banner;
                const cal = calendars.get(ev.calendarId);
                return (
                  <div key={ev.id} className={evClass("ag-row", ev)} style={evStyle(ev, calendars)}>
                    <button className="ag-open" onClick={() => onOpen(ev)}>
                    <span className="ag-time tnum">
                      {banner ? (
                        t("common.allDay")
                      ) : (
                        <>
                          {dayKeyIn(ev.start, tz) === key ? timeFmt(ev.start, locale, tz) : "…"}
                          <small>{dayKeyIn(ev.end, tz) === key ? timeFmt(ev.end, locale, tz) : "…"}</small>
                        </>
                      )}
                    </span>
                    <span className="ag-dot" aria-hidden="true" />
                    <span className="ag-main">
                      <strong>{title(ev)}</strong>
                      <small>
                        {cal?.name}
                        {ev.location ? ` · ${ev.location}` : ""}
                      </small>
                    </span>
                    </button>
                    {ev.meetingUrl && (
                      <a className="join" href={ev.meetingUrl} target="_blank" rel="noreferrer">
                        {t("common.join")}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
      {canLater ? <div ref={sentinel} className="ag-sentinel" aria-busy={loading} /> : <p className="ag-edge muted">{t("cal.windowEnd")}</p>}
    </div>
  );
}

// ───── Cases (semaine compacte, façon Week Calendar) ─────

export function BoxesView({ days, events, tz, today, calendars, onOpen, onPickDay, onCreate, onSwipe }: ViewProps & { days: DayKey[]; events: EventView[]; onSwipe: (dir: 1 | -1) => void }) {
  const { t, locale } = useI18n();
  const title = useTitle();
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") swipe.current = { x: e.clientX, y: e.clientY };
  };
  const onUp = (e: PointerEvent) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(e.clientY - s.y) * 1.5) onSwipe(dx < 0 ? 1 : -1);
  };

  const box = (key: DayKey) => {
    const items = eventsOnDay(events, key, tz);
    return (
      <section key={key} className={`bx${key === today ? " is-today" : ""}${isWeekend(key) ? " is-we" : ""}`}>
        <h3>
          <button onClick={() => onPickDay(key, "agenda")}>
            {dayFmt(key, locale, { weekday: "short" })} <span className="tnum">{Number(key.slice(8))}</span>
          </button>
        </h3>
        <div className={`bx-list${onCreate ? " can-create" : ""}`} onClick={(e) => onCreate && e.target === e.currentTarget && onCreate(key, null)}>
          {items.map((ev) =>
            spanOf(ev, tz).banner ? (
              <button key={ev.id} className={evClass("bx-bar", ev)} style={evStyle(ev, calendars)} onClick={() => onOpen(ev)}>
                {title(ev)}
              </button>
            ) : (
              <button key={ev.id} className={evClass("bx-ev", ev)} style={evStyle(ev, calendars)} onClick={() => onOpen(ev)}>
                <span className="tm tnum">{dayKeyIn(ev.start, tz) === key ? timeFmt(ev.start, locale, tz) : "…"}</span>
                <span className="tt">{title(ev)}</span>
              </button>
            ),
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="bxs" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={() => (swipe.current = null)}>
      <div className="bx-col">{days.slice(0, 4).map(box)}</div>
      <div className="bx-col">
        {days.slice(4).map(box)}
        <section className="bx bx-week" aria-hidden="true">
          <span className="wn tnum">{isoWeek(days[0]!)}</span>
          <span className="muted">{t("cal.weekNumber", { n: String(isoWeek(days[0]!)) })}</span>
        </section>
      </div>
    </div>
  );
}

// ───── Bandeau de la vue agenda : une semaine, dépliable en mois ─────

export function AgendaStrip({
  anchor,
  today,
  tz,
  weekStart,
  shareIds,
  calendars,
  onPick,
}: {
  anchor: DayKey;
  today: DayKey;
  tz: string;
  weekStart: number;
  shareIds: string[];
  calendars: Map<string, CalendarInfo>;
  onPick: (d: DayKey) => void;
}) {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [first, setFirst] = useState(() => startOfWeek(anchor, weekStart));
  useEffect(() => setFirst(startOfWeek(anchor, weekStart)), [anchor, weekStart]);
  const count = open ? 42 : 7;
  const days = Array.from({ length: count }, (_, i) => addDays(first, i));
  const query = useEvents(first, addDays(first, count), tz, shareIds);
  const dots = useMemo(() => {
    const map = new Map<DayKey, string[]>();
    for (const ev of query.data ?? []) {
      const cal = calendars.get(ev.calendarId);
      if (cal?.shown === false) continue;
      for (const k of days) if (eventsOnDay([ev], k, tz).length) map.set(k, [...(map.get(k) ?? []), cal?.color ?? "var(--accent)"]);
    }
    return map;
  }, [query.data, first, count, calendars]);
  // Tirer la poignée vers le bas déplie, vers le haut replie.
  const drag = useRef<number | null>(null);
  return (
    <div className="strip">
      <div className="strip-grid">
        {days.slice(0, 7).map((k) => (
          <span key={k} className="dow" aria-hidden="true">
            {dayFmt(k, locale, { weekday: "narrow" })}
          </span>
        ))}
        {days.map((k) => (
          <button
            key={k}
            className={[k === today && "today", k === anchor && "sel", k < today && "past"].filter(Boolean).join(" ")}
            aria-label={dayFmt(k, locale, { weekday: "long", day: "numeric", month: "long" })}
            onClick={() => onPick(k)}
          >
            {k.endsWith("-01") && <span className="mo">{dayFmt(k, locale, { month: "short" })}</span>}
            <span className="n">{Number(k.slice(8))}</span>
            <span className="dots" aria-hidden="true">
              {[...new Set(dots.get(k) ?? [])].slice(0, 3).map((c) => (
                <i key={c} style={{ background: c }} />
              ))}
            </span>
          </button>
        ))}
      </div>
      <div className="strip-bar">
        {open && (
          <button className="btn btn-sm btn-ghost" aria-label={t("cal.prev")} onClick={() => setFirst(addDays(first, -28))}>
            ‹
          </button>
        )}
        <button
          className="strip-handle"
          aria-expanded={open}
          aria-label={t("cal.toggleStrip")}
          onClick={() => setOpen((o) => !o)}
          onPointerDown={(e) => (drag.current = e.clientY)}
          onPointerUp={(e) => {
            if (drag.current !== null && Math.abs(e.clientY - drag.current) > 20) setOpen(e.clientY > drag.current);
            drag.current = null;
          }}
        >
          <span />
        </button>
        {open && (
          <button className="btn btn-sm btn-ghost" aria-label={t("cal.next")} onClick={() => setFirst(addDays(first, 28))}>
            ›
          </button>
        )}
      </div>
    </div>
  );
}
